import { PLANE_DEFAULT_Z } from '@Components/BigBrowse/BigBrowseConfig';
import type { Position2D } from '@Components/BigBrowse/Model/ModelTypes';
import { Assets, useAssetsStore } from '@Utils/AssetsStore';
import { Text as TroikaText } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import React, { useEffect, useRef } from 'react';

export type CardTextProps = {
  text: string;
  fontAsset?: Assets;
  color?: string;
  fontSize?: number;
  position?: Position2D;
  anchorX?: number | 'center' | 'left' | 'right';
  anchorY?:
    | number
    | 'bottom-baseline'
    | 'bottom'
    | 'middle'
    | 'top-baseline'
    | 'top';
  letterSpacing?: number;
  maxWidth?: number;
  textAlign?: 'center' | 'justify' | 'left' | 'right';
  fillOpacity?: number;
  zPosition?: number;
  lineHeight?: number;
};

type Props = CardTextProps & {
  onHeightChange?: (height: number) => void;
  visible?: boolean;
};

export const CardText = ({
  text,
  fontAsset = Assets.Font_Gilroy_Medium,
  color = 'white',
  position = { x: 0, y: 0 },
  fontSize = 12,
  zPosition = PLANE_DEFAULT_Z + 0.0001,
  onHeightChange,
  visible = true,
  ...rest
}: Props) => {
  const ref = useRef<typeof TroikaText>();
  const { gl, scene, camera } = useThree();
  const { get, loaded } = useAssetsStore();

  useEffect(() => {
    if (onHeightChange && loaded && ref.current) {
      setTimeout(() => {
        // @ts-expect-error
        const { visibleBounds } = ref.current?.textRenderInfo ?? {};
        const height = visibleBounds?.[3] - visibleBounds?.[1]; // visible bounds = [left, top, right, bottom]
        onHeightChange?.(height);
      }, 100); // just give a small-time for the text to render
    }
  }, [loaded, text, onHeightChange]);

  if (!loaded) {
    return null;
  }

  const font = get(fontAsset);

  return (
    // @ts-expect-error
    <TroikaText
      ref={ref}
      position={[position.x, position.y, zPosition]}
      color={color}
      font={font?.uri}
      fontSize={fontSize}
      onSync={() => {
        gl.render(scene, camera);
      }} // helps re-render when font-glyphs are done
      visible={visible}
      {...rest}
    >
      {text}
    </TroikaText>
  );
};
